/**
 * @fileoverview Percentage Distribution Utility
 * Handles intelligent percentage redistribution when tasks are excluded from plans
 */

/**
 * Redistributes percentages when some tasks are excluded from a plan
 * 
 * This function takes all tasks from a production plan and redistributes the percentages
 * of excluded tasks proportionally among the tasks that will actually be used. This ensures
 * that the total percentage always equals 100% even when some tasks are excluded due to
 * order-specific requirements.
 * 
 * @param {Array} centroCostoPesoArray - Array of centro_costo_peso objects with original percentages
 * @param {Array} usedCentroCostoIds - Array of centro_costo IDs that will be used
 * @returns {Array} Array of centro_costo_peso objects with redistributed percentages
 * 
 * @example
 * const centroCostoPesoArray = [
 *   { centro_costo: 'cc1', peso: 40 },
 *   { centro_costo: 'cc2', peso: 30 },
 *   { centro_costo: 'cc3', peso: 30 }
 * ];
 * const usedCentroCostoIds = ['cc2', 'cc3'];
 * const result = redistributePercentages(centroCostoPesoArray, usedCentroCostoIds);
 * // Result: tasks with cc2 and cc3 having 50% each (30% + 20% from excluded cc1)
 */
export const redistributePercentages = (centroCostoPesoArray, usedCentroCostoIds) => {
  // Validate inputs
  if (!Array.isArray(centroCostoPesoArray) || !Array.isArray(usedCentroCostoIds)) {
    throw new Error('Both centroCostoPesoArray and usedCentroCostoIds must be arrays');
  }

  // Handle empty arrays
  if (centroCostoPesoArray.length === 0 || usedCentroCostoIds.length === 0) {
    return [];
  }

  // Filter tasks that will be used
  const usedTasks = centroCostoPesoArray.filter(task => 
    usedCentroCostoIds.includes(task.centro_costo)
  );

  if (usedTasks.length === 0) {
    return [];
  }

  // Calculate total percentage of excluded tasks
  const excludedTasks = centroCostoPesoArray.filter(task => 
    !usedCentroCostoIds.includes(task.centro_costo)
  );
  const excludedPercentage = excludedTasks.reduce((sum, task) => sum + (task.peso || 0), 0);

  // If no excluded tasks, return original percentages
  if (excludedPercentage === 0) {
    return usedTasks.map(task => ({ ...task }));
  }

  // Calculate proportional distribution
  const redistributedTasks = calculateProportionalDistribution(usedTasks, excludedPercentage);

  // Distribute rounding errors to ensure 100% total
  const finalTasks = distributeRoundingErrors(redistributedTasks);

  // Validate final result
  if (!validatePercentageTotal(finalTasks)) {
    console.warn('Percentage redistribution did not result in 100% total');
  }

  return finalTasks;
};

/**
 * Calculates proportional distribution of excluded percentage among used tasks
 * 
 * This function distributes the percentage from excluded tasks proportionally
 * among the tasks that will be used, based on their original percentage weights.
 * 
 * @param {Array} usedTasks - Array of tasks that will be used
 * @param {number} excludedPercentage - Total percentage from excluded tasks
 * @returns {Array} Array of tasks with redistributed percentages
 * 
 * @example
 * const usedTasks = [
 *   { centro_costo: 'cc2', peso: 30 },
 *   { centro_costo: 'cc3', peso: 30 }
 * ];
 * const excludedPercentage = 40;
 * const result = calculateProportionalDistribution(usedTasks, excludedPercentage);
 * // Result: tasks with 50% each (30% + 20% from excluded percentage)
 */
export const calculateProportionalDistribution = (usedTasks, excludedPercentage) => {
  if (usedTasks.length === 0 || excludedPercentage === 0) {
    return usedTasks.map(task => ({ ...task }));
  }

  // Calculate total percentage of used tasks
  const usedPercentage = usedTasks.reduce((sum, task) => sum + (task.peso || 0), 0);
  
  if (usedPercentage === 0) {
    // If all used tasks have 0%, distribute excluded percentage equally
    const equalDistribution = Math.floor(excludedPercentage / usedTasks.length);
    return usedTasks.map(task => ({
      ...task,
      peso: equalDistribution
    }));
  }

  // Calculate proportional distribution
  return usedTasks.map(task => {
    const originalPeso = task.peso || 0;
    const proportion = originalPeso / usedPercentage;
    const additionalPeso = Math.floor(excludedPercentage * proportion);
    
    return {
      ...task,
      peso: originalPeso + additionalPeso
    };
  });
};

/**
 * Distributes rounding errors to ensure total percentage equals 100%
 * 
 * This function handles integer rounding errors by distributing remainders
 * to ensure the total percentage always equals exactly 100%.
 * 
 * @param {Array} tasks - Array of tasks with calculated percentages
 * @returns {Array} Array of tasks with final percentages totaling 100%
 * 
 * @example
 * const tasks = [
 *   { centro_costo: 'cc1', peso: 33 },
 *   { centro_costo: 'cc2', peso: 33 },
 *   { centro_costo: 'cc3', peso: 33 }
 * ];
 * const result = distributeRoundingErrors(tasks);
 * // Result: tasks with percentages that sum to exactly 100%
 */
export const distributeRoundingErrors = (tasks) => {
  if (tasks.length === 0) {
    return [];
  }

  // Calculate current total
  const currentTotal = tasks.reduce((sum, task) => sum + (task.peso || 0), 0);
  const remainder = 100 - currentTotal;

  if (remainder === 0) {
    return tasks.map(task => ({ ...task }));
  }

  // Create a copy of tasks to avoid mutating original
  const result = tasks.map(task => ({ ...task }));

  // Distribute remainder by adding 1 to tasks in order until remainder is 0
  let remainingToDistribute = remainder;
  let taskIndex = 0;

  while (remainingToDistribute > 0 && taskIndex < result.length) {
    result[taskIndex].peso = (result[taskIndex].peso || 0) + 1;
    remainingToDistribute--;
    taskIndex = (taskIndex + 1) % result.length;
  }

  // Handle negative remainder (shouldn't happen with proper input)
  while (remainingToDistribute < 0 && taskIndex < result.length) {
    if (result[taskIndex].peso > 0) {
      result[taskIndex].peso = result[taskIndex].peso - 1;
      remainingToDistribute++;
    }
    taskIndex = (taskIndex + 1) % result.length;
  }

  return result;
};

/**
 * Validates that the total percentage of tasks equals 100%
 * 
 * This function checks if the sum of all task percentages equals exactly 100%.
 * It's used to validate the correctness of percentage redistribution.
 * 
 * @param {Array} tasks - Array of tasks with percentages
 * @returns {boolean} True if total equals 100%, false otherwise
 * 
 * @example
 * const tasks = [
 *   { centro_costo: 'cc1', peso: 50 },
 *   { centro_costo: 'cc2', peso: 50 }
 * ];
 * const isValid = validatePercentageTotal(tasks);
 * // Result: true (50 + 50 = 100)
 */
export const validatePercentageTotal = (tasks) => {
  if (!Array.isArray(tasks)) {
    return false;
  }

  // Empty array is considered valid (0% total)
  if (tasks.length === 0) {
    return true;
  }

  const total = tasks.reduce((sum, task) => sum + (task.peso || 0), 0);
  return total === 100;
};

/**
 * Calculates the total percentage of a task array
 * @param {Array} tasks - Array of tasks with percentages
 * @returns {number} Total percentage
 */
export const calculateTotalPercentage = (tasks) => {
  if (!Array.isArray(tasks)) {
    return 0;
  }

  return tasks.reduce((sum, task) => sum + (task.peso || 0), 0);
};
